import { ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClass: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  size = 'md',
}: ModalProps) {
  const { t } = useTranslation();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink-900/30 data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content
          aria-describedby={description ? undefined : 'modal-no-desc'}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[95vw] -translate-x-1/2 -translate-y-1/2',
            'rounded-2xl bg-white border border-ink-200',
            'focus:outline-none',
            sizeClass[size],
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-ink-200">
            <div className="min-w-0">
              <Dialog.Title className="serif-jp text-base font-semibold text-ink-900">
                {title}
              </Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-1 text-sm text-ink-600">
                  {description}
                </Dialog.Description>
              ) : (
                <Dialog.Description id="modal-no-desc" className="sr-only">
                  {typeof title === 'string' ? title : t('common.dialog')}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              className="shrink-0 rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
              aria-label={t('common.close')}
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>
          <div className="px-6 py-5">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
